import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

export type StoryDesign = "klassik" | "rein" | "stadion" | "blitz";
export type StoryType = "matchday" | "goal" | "endergebnis";

const LIVE_BASE = "https://api.torflash.de/preview";

export interface StoryEmbedProps {
  design?: StoryDesign;
  type: StoryType;
  params?: Record<string, string | number | undefined>;
  title?: string;
  className?: string;
  lazy?: boolean;
  /** Stretch to fill the parent instead of enforcing a 9:16 box */
  fill?: boolean;
}

export function buildStoryUrl(
  design: StoryDesign,
  type: StoryType,
  params: Record<string, string | number | undefined> = {},
) {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (k !== "once" && k !== "still" && k !== "scale" && v !== undefined && v !== null) p.set(k, String(v));
  });
  p.set("scale", "0.5");
  const qs = p.toString();
  return `${LIVE_BASE}/${design}/${type}${qs ? `?${qs}` : ""}`;
}

export default function StoryEmbed({
  design = "klassik",
  type,
  params,
  title,
  className = "",
  lazy = true,
  fill = false,
}: StoryEmbedProps) {
  const src = buildStoryUrl(design, type, params);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [src]);

  return (
    <div className={`relative w-full overflow-hidden bg-muted ${className}`} style={fill ? { height: "100%" } : { aspectRatio: "9 / 16" }}>
      <img
        key={src}
        src={src}
        alt={title ?? `Design ${design}`}
        width="1080"
        height="1920"
        loading={lazy ? "lazy" : "eager"}
        decoding="async"
        className={`h-full w-full object-cover transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
        onLoad={() => setLoaded(true)}
      />
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}

/* Shared sample data used for public marketing embeds */
const SAMPLE_BASE = {
  club: "FC Musterstadt 1904",
  opp: "SV Beispielkirchen",
  league: "Bezirksliga",
  pc: "#1a56d6",
  crest: "",
  crestAway: "",
};

export const SAMPLE_PARAMS: Record<StoryType, Record<string, string>> = {
  matchday: { ...SAMPLE_BASE, date: "15.08.2026", time: "15:00", venue: "Sportpark" },
  goal: {
    ...SAMPLE_BASE,
    scorer: "L. Beispiel",
    min: "72",
    homeScore: "1",
    awayScore: "0",
    playerImage: "https://api.torflash.de/tfassets/dcassets/cutouts/jubel-1.png",
  },
  endergebnis: {
    ...SAMPLE_BASE,
    homeScore: "3",
    awayScore: "1",
    scorersHome: "7' L. Beispiel\n45' L. Beispiel\n78' L. Beispiel",
    scorersAway: "44' T. Beispiel",
  },
};
